import React, { useEffect, useMemo, useState } from "react";
import "../../styles/App.css";
import {
  listDormitories,
  listRooms,
  previewImport,
  getImportBatch,
  patchImportRow,
  setImportDefaultZone,
  commitImport,
  confirmImport,
  revertImport,
  ImportApiError,
} from "../../api/importRating.js";
import PrivilegeSelect from "./PrivilegeSelect.js";
import SuccessDialog from "../../components/SuccessDialog.js";
import DefaultZoneBlock, { ZONE_OPTIONS } from "./DefaultZoneBlock.js";

const RUN_TYPES = [
  { value: "settlement", label: "Поселення на новий навчальний рік" },
  { value: "relocation", label: "Переселення за підсумками семестру" },
];

const STATUS_LABELS = {
  matched: "знайдено",
  new: "новий",
  ambiguous: "неоднозначно",
  rejected: "відхилено",
};

const ISSUE_LABELS = {
  empty: "не вказано",
  clamped: "обрізано до максимуму",
  out_of_range: "значення поза шкалою",
  no_scores: "немає балів",
  no_session: "без сесії (бал нижче 30 або не вказано)",
  pib_empty: "порожній ПІБ",
  zone_missing: "віддаленість не вказана",
  zone_default: "віддаленість взято за замовчуванням",
  privilege_default: "пільгу не вказано, прийнято 1 (без пільги)",
  room_format: "номер кімнати записано нестандартно",
  room_not_found: "кімнату не знайдено в системі",
  room_changed: "у системі інша кімната",
  ambiguous: "кілька збігів",
  duplicate_in_file: "дубль у файлі",
  excluded_manual: "прибрано з імпорту",
  not_in_dormitory: "немає кімнати в цьому гуртожитку",
};

const GENDER_OPTIONS = [
  { value: "male", label: "чол." },
  { value: "female", label: "жін." },
];

const PAGE_SIZES = [25, 50, 100, 200];

const FIELD_LABELS = {
  ses: "бал за сесію",
  dorm: "бал за гуртожиток",
  act: "бал за діяльність",
  zone: "віддаленість",
};

// Назву поля додаємо лише до проблем із балами; решта підписів самодостатні.
function issueText(issue) {
  const base = ISSUE_LABELS[issue.type] || issue.type;
  const field = FIELD_LABELS[issue.field];
  return field && ["empty", "clamped", "out_of_range"].includes(issue.type) ? `${base}: ${field}` : base;
}

// Початок навчального року (1 вересня) для сьогоднішньої дати.
function defaultPeriod(today = new Date()) {
  const year =
    today.getMonth() >= 8 ? today.getFullYear() : today.getFullYear() - 1;
  const iso = (d) => d.toISOString().slice(0, 10);
  return { start: `${year}-09-01`, end: iso(today) };
}

function errorMessage(err) {
  if (err instanceof ImportApiError) return `${err.message} (${err.status})`;
  return "Не вдалося виконати запит. Спробуйте ще раз.";
}

// Спосіб фільтра: "issue:<тип>" або "status:<статус>".
function matchesFilter(row, filter) {
  if (!filter) return true;
  if (filter === "no_room") return row.matchStatus !== "rejected" && !row.roomId;
  const [kind, value] = filter.split(":");
  if (kind === "status") return row.matchStatus === value;
  return row.issues.some((i) => i.type === value);
}

// Підсумок для вікна про успішну фіксацію.
function commitSummary(st, studentsMode, autoCalc = false) {
  const created = st?.new ?? 0;
  const updated = st?.matched ?? 0;
  const skipped = st?.rejected ?? 0;
  const lines = [`Нових студентів створено: ${created}`, `Наявних оновлено: ${updated}`];
  if (!studentsMode) lines.push(`Знімків балів записано: ${created + updated}`);
  if (skipped > 0) lines.push(`Не імпортовано (відхилені або прибрані рядки): ${skipped}`);
  return {
    title: studentsMode ? "Студентів успішно імпортовано" : "Рейтинг успішно імпортовано",
    lines,
    note: studentsMode
      ? "Відкотити цей імпорт можна в меню «Відкотити останній імпорт»."
      : `${autoCalc ? "Розрахунок за зафіксованими даними запущено автоматично — звіт нижче. " : ""}Наступний крок — «Підтвердити бали». Відкотити імпорт можна в меню «Відкотити останній імпорт».`,
  };
}

// Завантаження рейтингу в систему: попередній перегляд на сервері (нічого не
// змінює), ручне доповнення, фіксація, підтвердження балів і відкат. На сервер
// надсилаються лише розпізнані рядки і хеш файлу, сирий файл -- ніколи.
// Цикл сортування за стовпцем: зростання -> спадання -> без сортування.
function nextSort(cur, key) {
  if (!cur || cur.key !== key) return { key, dir: "asc" };
  return cur.dir === "asc" ? { key, dir: "desc" } : null;
}

function sortAria(sort, key) {
  if (sort?.key !== key) return "none";
  return sort.dir === "asc" ? "ascending" : "descending";
}

function sortArrow(sort, key) {
  if (sort?.key !== key) return "";
  return sort.dir === "asc" ? " ▲" : " ▼";
}

// Порожні значення завжди внизу: xe/ye -- чи порожнє значення.
function compareBlank(xe, ye) {
  if (xe === ye) return 0;
  return xe ? 1 : -1;
}

// mode="students": окремий імпорт студентів (список факультету) без балів і
// періоду; "rating" (типово) - імпорт рейтингу з балами.
// Кімната в системі: спершу гуртожиток, після вибору -- кімната. Початкове
// значення -- номер з файлу в основному гуртожитку (його ставить сервер).
function SystemRoomCell({ row, dormitories, rooms, editable, busy, onPatch }) {
  if (row.matchStatus === "rejected") return <td className="table-content">—</td>;
  const dormitory = dormitories.find((d) => d.id === row.dormitoryId);
  if (!editable) {
    const room = (rooms[row.dormitoryId] || []).find((r) => r.id === row.roomId);
    let label = "—";
    if (dormitory) label = room ? `${dormitory.name}, ${room.number}` : dormitory.name;
    return <td className="table-content">{label}</td>;
  }
  return (
    <td className="table-content">
      <select
        className="header-content-select"
        aria-label={`Гуртожиток для рядка ${row.rowNumber}`}
        value={row.dormitoryId ?? ""}
        disabled={busy}
        onChange={(e) =>
          onPatch(row.rowNumber, {
            dormitoryId: e.target.value ? Number(e.target.value) : null,
            roomId: null,
          })
        }
      >
        <option value="">— гуртожиток —</option>
        {dormitories.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>
      {row.dormitoryId && (
        <select
          className="header-content-select"
          aria-label={`Кімната для рядка ${row.rowNumber}`}
          value={row.roomId ?? ""}
          disabled={busy || !rooms[row.dormitoryId]}
          onChange={(e) =>
            onPatch(row.rowNumber, {
              dormitoryId: row.dormitoryId,
              roomId: e.target.value ? Number(e.target.value) : null,
            })
          }
        >
          <option value="">— кімната —</option>
          {(rooms[row.dormitoryId] || []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.number}
            </option>
          ))}
        </select>
      )}
    </td>
  );
}

const ImportToSystemPanel = ({
  rows,
  fileHash,
  sheetName,
  anonymize,
  defaultRunType = "settlement",
  disabledReason,
  mode = "rating",
  onCommitted,
  // Віддаленість за замовчуванням зі сторінки (та сама, що в «Розрахувати»):
  // застосовується й до пакета, а задана тут -- повертається на сторінку
  // (onDefaultZone), щоб розрахунок і дані в системі мали одне значення.
  defaultZone: pageDefaultZone = null,
  onDefaultZone,
}) => {
  const studentsMode = mode === "students";
  const [open, setOpen] = useState(false);
  const [dormitories, setDormitories] = useState([]);
  const [runType, setRunType] = useState(defaultRunType);
  const initialPeriod = useMemo(() => defaultPeriod(), []);
  const [periodStart, setPeriodStart] = useState(initialPeriod.start);
  const [periodEnd, setPeriodEnd] = useState(initialPeriod.end);

  const [batch, setBatch] = useState(null); // { batchId, status, stats, rows }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [success, setSuccess] = useState(null);
  const [confirmedCount, setConfirmedCount] = useState(null);
  const [revertAsked, setRevertAsked] = useState(false);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // Пакет, який уже зафіксовано з цим файлом (409): його можна відкотити й завантажити заново.
  const [staleBatchId, setStaleBatchId] = useState(null);
  // Сортування таблиці: { key, dir: "asc" | "desc" } або null.
  const [sort, setSort] = useState(null);
  // Лише студенти з кімнатою в обраному гуртожитку (mode=students).
  const [onlyResidents, setOnlyResidents] = useState(false);
  const [ambiguousChoice, setAmbiguousChoice] = useState({});
  // Кімнати за id гуртожитку: { [dormitoryId]: [{ id, number }] }.
  const [roomsByDormitory, setRoomsByDormitory] = useState({});

  useEffect(() => {
    if (!open || dormitories.length > 0) return;
    let cancelled = false;
    listDormitories()
      .then((data) => {
        if (cancelled || !Array.isArray(data)) return;
        setDormitories(data);
      })
      .catch((err) => {
        if (!cancelled)
          setError(`Не вдалося завантажити гуртожитки. ${errorMessage(err)}`);
      });
    return () => {
      cancelled = true;
    };
  }, [open, dormitories.length]);

  const batchDormitoryIds = useMemo(
    () => [...new Set((batch?.rows || []).map((r) => r.dormitoryId).filter(Boolean))],
    [batch]
  );
  useEffect(() => {
    batchDormitoryIds
      .filter((id) => !roomsByDormitory[id])
      .forEach((id) => {
        listRooms(id)
          .then((list) => setRoomsByDormitory((prev) => ({ ...prev, [id]: list })))
          .catch((err) => setError(`Не вдалося завантажити кімнати. ${errorMessage(err)}`));
      });
  }, [batchDormitoryIds, roomsByDormitory]);

  // Гуртожиток за замовчуванням задається в «Керуванні гуртожитками»; кімнату
  // кожного рядка можна змінити в колонці «Кімната в системі».
  const mainDormitory = dormitories.find((d) => d.is_main) || null;
  const dormitoryId = mainDormitory ? String(mainDormitory.id) : "";

  const run = async (action) => {
    setBusy(true);
    setError("");
    setNotice("");
    setStaleBatchId(null);
    try {
      await action();
    } catch (err) {
      console.error(err);
      setError(errorMessage(err));
      if (err instanceof ImportApiError && err.status === 409 && err.data?.batchId) {
        setStaleBatchId(err.data.batchId);
      }
    } finally {
      setBusy(false);
    }
  };

  const reload = async (batchId) => {
    const fresh = await getImportBatch(batchId);
    setBatch(fresh);
    return fresh;
  };

  const handlePreview = () => {
    if (!dormitoryId) {
      return setError(
        "Не задано гуртожиток за замовчуванням: позначте його в «Керуванні гуртожитками».",
      );
    }
    if (
      !studentsMode &&
      (!periodStart || !periodEnd || periodStart > periodEnd)
    ) {
      return setError("Вкажіть коректний період.");
    }
    if (!fileHash) return setError("Немає хешу файлу: оберіть файл ще раз.");
    return run(async () => {
      const common = {
        fileSha256: fileHash,
        sheetName: sheetName || "Аркуш1",
        dormitoryId: Number(dormitoryId),
        rows,
      };
      const result = await previewImport(
        studentsMode
          ? {
              ...common,
              mode: "students",
              onlyDormitoryResidents: onlyResidents,
            }
          : { ...common, runType, periodStart, periodEnd },
      );
      setBatch({
        batchId: result.batchId,
        status: result.status,
        stats: result.stats,
        rows: result.rows,
      });
      if (pageDefaultZone !== null && result.stats?.zoneMissing > 0) {
        const res = await setImportDefaultZone(result.batchId, pageDefaultZone);
        await reload(result.batchId);
        setNotice(
          `Віддаленість за замовчуванням (${pageDefaultZone}) з розрахунку застосовано до ${res.updated} рядків.`,
        );
      }
      setConfirmedCount(null);
      setRevertAsked(false);
      setFilter("");
    });
  };

  const handlePatch = (rowNumber, patch) =>
    run(async () => {
      await patchImportRow(batch.batchId, rowNumber, patch);
      await reload(batch.batchId);
    });

  const handleDefaultZone = (zone) =>
    run(async () => {
      const res = await setImportDefaultZone(batch.batchId, zone);
      await reload(batch.batchId);
      if (typeof onDefaultZone === "function") onDefaultZone(zone);
      setNotice(
        `Віддаленість за замовчуванням задано для ${res.updated} рядків.`,
      );
    });

  const showOnly = (value) => {
    setFilter(value);
    setPage(1);
  };

  // Усі рядки без кімнати в системі -- з імпорту одним кроком (по одному PATCH на
  // рядок, як і кнопка «Видалити з імпорту»; повернути можна кнопкою «Повернути»).
  const handleExcludeNoRoom = () => {
    const targets = (batch?.rows || []).filter((r) => r.matchStatus !== "rejected" && !r.roomId);
    if (targets.length === 0) return undefined;
    if (!window.confirm(`Прибрати з імпорту всі рядки без кімнати в системі (${targets.length})? Їх можна буде повернути.`)) {
      return undefined;
    }
    return run(async () => {
      for (const r of targets) {
        await patchImportRow(batch.batchId, r.rowNumber, { excluded: true });
      }
      await reload(batch.batchId);
      setNotice(`Прибрано з імпорту рядків без кімнати: ${targets.length}.`);
    });
  };

  const handleCommit = () =>
    run(async () => {
      const res = await commitImport(batch.batchId);
      const fresh = await reload(batch.batchId);
      setNotice(
        studentsMode
          ? "Студентів збережено в системі."
          : "Рейтинг зафіксовано в системі.",
      );
      setSuccess(commitSummary(res?.stats || stats, studentsMode, typeof onCommitted === "function"));
      if (typeof onCommitted === "function") onCommitted(fresh);
    });

  const handleConfirm = () =>
    run(async () => {
      const res = await confirmImport(batch.batchId);
      setConfirmedCount(res.confirmed);
      setNotice(`Бали підтверджено: ${res.confirmed}.`);
    });

  const handleRevert = () =>
    run(async () => {
      const res = await revertImport(batch.batchId);
      await reload(batch.batchId);
      setRevertAsked(false);
      setNotice(
        `Імпорт відкочено: відновлено ${res.restored}, видалено нових ${res.deleted}.`,
      );
    });

  // Відкат попереднього імпорту цього самого файлу, щоб завантажити його заново.
  const handleRevertStale = () => {
    const batchId = staleBatchId;
    if (
      !window.confirm(
        "Відкотити попередній імпорт цього файлу? Здобувачів, створених ним, буде видалено, а змінені дані відновлено.",
      )
    ) {
      return;
    }
    return run(async () => {
      const res = await revertImport(batchId);
      setNotice(
        `Попередній імпорт відкочено: відновлено ${res.restored}, видалено ${res.deleted}. Натисніть «Показати попередній перегляд».`,
      );
    });
  };

  const handleReset = () => {
    setBatch(null);
    setError("");
    setNotice("");
    setConfirmedCount(null);
    setRevertAsked(false);
  };

  const toggleSort = (key) => {
    setPage(1);
    setSort((cur) => nextSort(cur, key));
  };
  const sortTh = (k, children) => (
    <th
      key={k}
      className="table-header"
      style={{ cursor: "pointer", userSelect: "none" }}
      aria-sort={sortAria(sort, k)}
      title="Натисніть, щоб відсортувати"
      onClick={() => toggleSort(k)}
    >
      {children}
      {sortArrow(sort, k)}
    </th>
  );

  const stats = batch?.stats;
  const status = batch?.status;
  const batchRows = useMemo(() => batch?.rows || [], [batch]);
  // Список студентів: у кожного має бути кімната в системі -- інакше рядок прибирають.
  const noRoomCount = studentsMode
    ? batchRows.filter((r) => r.matchStatus !== "rejected" && !r.roomId).length
    : 0;
  const blockers = stats ? stats.ambiguous + stats.zoneMissing + noRoomCount : 0;
  const acceptedCount = stats ? stats.total - stats.rejected : 0;
  const canCommit =
    status === "preview" && blockers === 0 && acceptedCount > 0 && !busy;
  const defaultRow = batchRows.find((r) => r.zoneIsDefault);

  // Значення за замовчуванням змінили на сторінці (для «Розрахувати»), поки
  // пакет у попередньому перегляді -> те саме значення й у пакеті.
  useEffect(() => {
    if (pageDefaultZone === null || status !== "preview" || !batch) return;
    const differs = stats?.zoneMissing > 0 || (defaultRow && defaultRow.zone !== pageDefaultZone);
    if (!differs) return;
    run(async () => {
      const res = await setImportDefaultZone(batch.batchId, pageDefaultZone);
      await reload(batch.batchId);
      setNotice(`Віддаленість за замовчуванням (${pageDefaultZone}) з розрахунку застосовано до ${res.updated} рядків.`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageDefaultZone]);

  const filterOptions = useMemo(() => {
    const issueTypes = new Set();
    const statuses = new Set();
    batchRows.forEach((r) => {
      statuses.add(r.matchStatus);
      r.issues.forEach((i) => issueTypes.add(i.type));
    });
    return [
      ...(studentsMode ? [{ value: "no_room", label: "Без кімнати в системі" }] : []),
      ...[...statuses].map((s) => ({
        value: `status:${s}`,
        label: `Статус: ${STATUS_LABELS[s] || s}`,
      })),
      ...[...issueTypes].map((t) => ({
        value: `issue:${t}`,
        label: `Проблема: ${ISSUE_LABELS[t] || t}`,
      })),
    ];
  }, [batchRows, studentsMode]);

  const roomNumberOf = (r) =>
    (roomsByDormitory[r.dormitoryId] || []).find((x) => x.id === r.roomId)?.number || "";
  const dormitoryNameOf = (r) => dormitories.find((d) => d.id === r.dormitoryId)?.name || "";
  const SORT_VALUES = {
    rowNumber: (r) => r.rowNumber,
    pib: (r) => r.pib || "",
    room: (r) => r.room || "",
    systemRoom: (r) => `${dormitoryNameOf(r)} ${roomNumberOf(r)}`.trim(),
    group: (r) => r.group || "",
    ses: (r) => r.ses,
    dorm: (r) => r.dorm,
    act: (r) => r.act,
    zone: (r) => r.zone,
    privilege: (r) => r.privilege,
    gender: (r) => r.gender || "",
    course: (r) => r.course,
    status: (r) => STATUS_LABELS[r.matchStatus] || r.matchStatus,
    issues: (r) => r.issues.length,
  };
  const visibleRows = useMemo(() => {
    const filtered = batchRows.filter((r) => matchesFilter(r, filter));
    if (!sort) return filtered;
    const value = SORT_VALUES[sort.key];
    const dir = sort.dir === "asc" ? 1 : -1;
    // Порожні значення завжди внизу, незалежно від напрямку.
    return [...filtered].sort((a, b) => {
      const x = value(a);
      const y = value(b);
      const xe = x === null || x === undefined || x === "";
      const ye = y === null || y === undefined || y === "";
      if (xe || ye) return compareBlank(xe, ye);
      if (typeof x === "number" && typeof y === "number") return (x - y) * dir;
      return String(x).localeCompare(String(y), "uk", { numeric: true }) * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchRows, filter, sort, roomsByDormitory, dormitories]);
  const editable = status === "preview";
  const totalPages = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = visibleRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (!open) {
    return (
      <>
        <button
          type="button"
          className="submit-button"
          disabled={Boolean(disabledReason)}
          title={disabledReason || undefined}
          onClick={() => setOpen(true)}
        >
          Завантажити в систему
        </button>
        {disabledReason && <p className="text-header">{disabledReason}</p>}
      </>
    );
  }

  return (
    <div className="input-component" data-testid="import-to-system">
      <SuccessDialog
        open={Boolean(success)}
        title={success?.title}
        lines={success?.lines}
        note={success?.note}
        onClose={() => setSuccess(null)}
      />
      <h3 className="text-header">
        {studentsMode
          ? "Завантаження студентів у систему"
          : "Завантаження рейтингу в систему"}
      </h3>
      <p className="text-header">
        На сервер надсилаються лише розпізнані рядки та хеш файлу, сам файл не
        передається. Попередній перегляд нічого не змінює в базі.
      </p>

      {!batch && (
        <div className="form-container">
          {studentsMode && (
            <div className="input-block text-header">
              <p className="text-header">Кого імпортувати:</p>
              <select
                className="header-content-select"
                aria-label="Кого імпортувати"
                value={onlyResidents ? "residents" : "all"}
                onChange={(e) =>
                  setOnlyResidents(e.target.value === "residents")
                }
              >
                <option value="all">
                  Усіх зі списку (кімнату кожному вкажіть вручну або видаліть рядок)
                </option>
                <option value="residents">
                  Лише мешканців гуртожитку за замовчуванням
                </option>
              </select>
            </div>
          )}
          {!studentsMode && (
            <div className="input-block text-header">
              <p className="text-header">Тип прогону:</p>
              <select
                className="header-content-select"
                aria-label="Тип прогону (завантаження)"
                value={runType}
                onChange={(e) => setRunType(e.target.value)}
              >
                {RUN_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!studentsMode && (
            <>
              <div className="input-block text-header">
                <p className="text-header">Період з:</p>
                <input
                  type="date"
                  className="input-component-with-border"
                  aria-label="Початок періоду"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                />
              </div>
              <div className="input-block text-header">
                <p className="text-header">до:</p>
                <input
                  type="date"
                  className="input-component-with-border"
                  aria-label="Кінець періоду"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                />
              </div>
            </>
          )}
          <button
            type="button"
            className="submit-button"
            disabled={busy}
            onClick={handlePreview}
          >
            {busy ? "Зачекайте..." : "Показати попередній перегляд"}
          </button>
          <button
            type="button"
            className="restore-button"
            onClick={() => setOpen(false)}
          >
            Закрити
          </button>
        </div>
      )}

      {error && (
        <p
          className="text-header"
          role="alert"
          style={{ color: "var(--full-busy)" }}
        >
          {error}
        </p>
      )}
      {staleBatchId && (
        <button type="button" className="restore-button" disabled={busy} onClick={handleRevertStale}>
          Відкотити попередній імпорт цього файлу і завантажити заново
        </button>
      )}
      {notice && <p className="text-header">{notice}</p>}

      {batch && (
        <>
          <div className="form-container stats-grid" data-testid="batch-counters">
            <p className="input-block text-header">
              <strong>Знайдено:</strong> {stats.matched}
            </p>
            <p className="input-block text-header">
              <strong>Нових:</strong> {stats.new}
            </p>
            <p className="input-block text-header">
              <strong>Неоднозначних:</strong> {stats.ambiguous}
            </p>
            <p className="input-block text-header">
              <strong>Відхилено:</strong> {stats.rejected}
            </p>
            <p className="input-block text-header">
              <strong>Без віддаленості:</strong> {stats.zoneMissing}
            </p>
            <p className="input-block text-header">
              <strong>Обрізано:</strong> {stats.clamped}
            </p>
          </div>

          {editable && (
            <DefaultZoneBlock
              testId="batch-zone-block"
              missingCount={stats.zoneMissing}
              defaultCount={stats.zoneDefault}
              defaultZone={
                stats.zoneMissing === 0 && defaultRow ? defaultRow.zone : null
              }
              onApply={handleDefaultZone}
              busy={busy}
            />
          )}

          <div className="form-container">
            <div className="input-block text-header">
              <select
                className="header-content-select"
                aria-label="Фільтр рядків"
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">Усі рядки</option>
                {filterOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="table-scroll">
          <table className="table import-table" data-testid="batch-table">
            <thead>
              <tr>
                {sortTh("rowNumber", "№")}
                {sortTh("pib", "ПІБ")}
                {!anonymize && sortTh("room", "Кімната у файлі")}
                {!anonymize && sortTh("systemRoom", "Кімната в системі")}
                {studentsMode ? (
                  sortTh("group", "Група")
                ) : (
                  <>
                    {sortTh("ses", "Сесія")}
                    {sortTh("dorm", "Гуртожиток")}
                    {sortTh("act", "Діяльність")}
                  </>
                )}
                {sortTh("zone", "Віддаленість")}
                {sortTh("privilege", "Пільга")}
                {sortTh("gender", "Стать")}
                {sortTh("course", "Курс")}
                {sortTh("status", "Статус")}
                {sortTh("issues", "Проблеми")}
                {editable && <th className="table-header">Дія</th>}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row) => {
                const rejected = row.matchStatus === "rejected";
                const canEdit = editable && !rejected && !busy;
                const candidates =
                  row.issues.find((i) => i.type === "ambiguous")?.candidates ||
                  [];
                const choice = ambiguousChoice[row.rowNumber] ?? "";
                return (
                  <tr
                    key={row.rowNumber}
                    style={
                      rejected
                        ? { backgroundColor: "var(--half-busy)" }
                        : undefined
                    }
                  >
                    <td className="table-content">{row.rowNumber}</td>
                    <td className="table-content cell-name">
                      {anonymize ? `Здобувач ${row.rowNumber}` : row.pib || "—"}
                    </td>
                    {!anonymize && (
                      <td className="table-content">{row.room || "—"}</td>
                    )}
                    {!anonymize && (
                      <SystemRoomCell
                        row={row}
                        dormitories={dormitories}
                        rooms={roomsByDormitory}
                        editable={editable}
                        busy={busy}
                        onPatch={handlePatch}
                      />
                    )}
                    {studentsMode ? (
                      <td className="table-content">{row.group || "—"}</td>
                    ) : (
                      <>
                        <td className="table-content">{row.ses ?? "—"}</td>
                        <td className="table-content">{row.dorm ?? "—"}</td>
                        <td className="table-content">{row.act ?? "—"}</td>
                      </>
                    )}
                    <td className="table-content">
                      {rejected ? (
                        "—"
                      ) : (
                        <select
                          className="header-content-select"
                          aria-label={`Віддаленість для рядка ${row.rowNumber}`}
                          value={row.zone ?? ""}
                          disabled={!canEdit}
                          onChange={(e) =>
                            handlePatch(row.rowNumber, {
                              zone: Number(e.target.value),
                            })
                          }
                        >
                          {row.zone === null && <option value="">—</option>}
                          {ZONE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="table-content">
                      {!rejected && (
                        <PrivilegeSelect
                          value={row.privilege}
                          ariaLabel={`Пільга для рядка ${row.rowNumber} (завантаження)`}
                          onChange={(value) =>
                            canEdit &&
                            handlePatch(row.rowNumber, { privilege: value })
                          }
                        />
                      )}
                    </td>
                    <td className="table-content">
                      {!rejected && (
                        <select
                          className="header-content-select"
                          aria-label={`Стать для рядка ${row.rowNumber}`}
                          value={row.gender ?? ""}
                          disabled={!canEdit}
                          onChange={(e) =>
                            e.target.value &&
                            handlePatch(row.rowNumber, {
                              gender: e.target.value,
                            })
                          }
                        >
                          <option value="">—</option>
                          {GENDER_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="table-content">
                      {!rejected && (
                        <select
                          className="header-content-select"
                          aria-label={`Курс для рядка ${row.rowNumber}`}
                          value={row.course ?? ""}
                          disabled={!canEdit}
                          onChange={(e) =>
                            e.target.value &&
                            handlePatch(row.rowNumber, {
                              course: Number(e.target.value),
                            })
                          }
                        >
                          <option value="">—</option>
                          {[1, 2, 3, 4, 5, 6].map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="table-content">
                      {STATUS_LABELS[row.matchStatus] || row.matchStatus}
                      {row.matchStatus === "ambiguous" && editable && (
                        <div>
                          <select
                            className="header-content-select"
                            aria-label={`Здобувач для рядка ${row.rowNumber}`}
                            value={choice}
                            onChange={(e) =>
                              setAmbiguousChoice((prev) => ({
                                ...prev,
                                [row.rowNumber]: e.target.value,
                              }))
                            }
                          >
                            <option value="">— оберіть —</option>
                            {candidates.map((id) => (
                              <option key={id} value={id}>
                                Здобувач №{id}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="restore-button"
                            disabled={!choice || busy}
                            onClick={() =>
                              handlePatch(row.rowNumber, {
                                studentId: Number(choice),
                              })
                            }
                          >
                            Обрати
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="table-content cell-issues">
                      {row.issues.map(issueText).join(", ") || "—"}
                    </td>
                    {editable && (
                      <td className="table-content">
                        {!rejected && (
                          <button
                            type="button"
                            className="restore-button"
                            disabled={busy}
                            aria-label={`Видалити рядок ${row.rowNumber} з імпорту`}
                            onClick={() => handlePatch(row.rowNumber, { excluded: true })}
                          >
                            Видалити з імпорту
                          </button>
                        )}
                        {rejected && row.issues.some((i) => i.type === "excluded_manual") && (
                          <button
                            type="button"
                            className="restore-button"
                            disabled={busy}
                            aria-label={`Повернути рядок ${row.rowNumber} в імпорт`}
                            onClick={() => handlePatch(row.rowNumber, { excluded: false })}
                          >
                            Повернути
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <div className="form-container" data-testid="pagination">
            <button
              type="button"
              className="restore-button"
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
            >
              ← Назад
            </button>
            <span className="input-block text-header">
              Сторінка {currentPage} з {totalPages} (рядків: {visibleRows.length})
            </span>
            <button
              type="button"
              className="restore-button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage(currentPage + 1)}
            >
              Далі →
            </button>
            <select
              className="header-content-select"
              aria-label="Рядків на сторінці"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n} на сторінці
                </option>
              ))}
            </select>
          </div>

          {status === "preview" && (
            <>
              <button
                type="button"
                className="submit-button"
                disabled={!canCommit}
                title={
                  blockers > 0
                    ? "Спершу розв'яжіть неоднозначні рядки, вкажіть віддаленість і кімнату (або видаліть рядок з імпорту)"
                    : undefined
                }
                onClick={handleCommit}
              >
                Зафіксувати
              </button>
              {blockers > 0 && (
                <p className="text-header" style={{ color: "var(--full-busy)" }} data-testid="commit-blockers">
                  Фіксація недоступна: неоднозначних — {stats.ambiguous}, без
                  віддаленості — {stats.zoneMissing}
                  {studentsMode ? `, без кімнати в системі — ${noRoomCount}` : ""}.
                </p>
              )}
              {stats.ambiguous > 0 && (
                <div className="no-room-help" data-testid="ambiguous-help">
                  <p className="text-header">
                    Для неоднозначних рядків оберіть здобувача в колонці «Статус» (кнопка «Обрати»).
                  </p>
                  <div className="no-room-actions">
                    <button type="button" className="restore-button" disabled={busy} onClick={() => showOnly("status:ambiguous")}>
                      Показати неоднозначні ({stats.ambiguous})
                    </button>
                  </div>
                </div>
              )}
              {stats.zoneMissing > 0 && (
                <div className="no-room-help" data-testid="zone-help">
                  <p className="text-header">
                    Вкажіть віддаленість у рядках або задайте її для всіх одразу кнопкою «Задати віддаленість за
                    замовчуванням» вище.
                  </p>
                  <div className="no-room-actions">
                    <button type="button" className="restore-button" disabled={busy} onClick={() => showOnly("issue:zone_missing")}>
                      Показати рядки без віддаленості ({stats.zoneMissing})
                    </button>
                  </div>
                </div>
              )}
              {noRoomCount > 0 && (
                <div className="no-room-help" data-testid="no-room-help">
                  <p className="text-header">
                    Для кожного рядка без кімнати оберіть гуртожиток і кімнату в колонці «Кімната в системі» або
                    приберіть рядок з імпорту.
                    {anonymize &&
                      " Зараз увімкнено «Знеособити» — колонку з кімнатами приховано; зніміть позначку, щоб обрати кімнати."}
                  </p>
                  <div className="no-room-actions">
                    <button
                      type="button"
                      className="restore-button"
                      disabled={busy}
                      onClick={() => showOnly("no_room")}
                    >
                      Показати рядки без кімнати ({noRoomCount})
                    </button>
                    <button type="button" className="remove-button" disabled={busy} onClick={handleExcludeNoRoom}>
                      Прибрати з імпорту всі рядки без кімнати ({noRoomCount})
                    </button>
                  </div>
                </div>
              )}
              <button
                type="button"
                className="restore-button"
                disabled={busy}
                onClick={handleReset}
              >
                Почати спочатку
              </button>
            </>
          )}

          {status === "committed" && (
            <div className="form-container">
              {!studentsMode && (
                <button
                  type="button"
                  className="submit-button"
                  disabled={busy || confirmedCount !== null}
                  onClick={handleConfirm}
                >
                  Підтвердити бали
                </button>
              )}
              {!revertAsked ? (
                <button
                  type="button"
                  className="restore-button"
                  disabled={busy}
                  onClick={() => setRevertAsked(true)}
                >
                  Відкотити
                </button>
              ) : (
                <>
                  <p className="text-header">
                    Відкотити імпорт? Нових здобувачів буде видалено.
                  </p>
                  <button
                    type="button"
                    className="submit-button"
                    disabled={busy}
                    onClick={handleRevert}
                  >
                    Так, відкотити
                  </button>
                  <button
                    type="button"
                    className="restore-button"
                    onClick={() => setRevertAsked(false)}
                  >
                    Ні
                  </button>
                </>
              )}
            </div>
          )}

          {status === "reverted" && (
            <button
              type="button"
              className="restore-button"
              onClick={handleReset}
            >
              Почати знову
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default ImportToSystemPanel;
